import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { fmtNOK, fmtDate, fmtKwh, fmtOre } from "@/lib/format";
import { ArrowLeft, Send, FileText, AlertCircle, XCircle, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Faktura() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [amount, setAmount] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
      setAmount(String(data.total_nok - (data.paid_nok || 0)));
    } catch { setInv({ error: true }); }
  };
  useEffect(() => { load(); }, [id]);

  if (!inv) return <Spinner />;
  if (inv.error) return <Empty title="Faktura ikke funnet" />;

  const openPdf = () => {
    window.open(`${API}/invoices/${id}/pdf`, "_blank");
  };

  const action = async (kind) => {
    setBusy(true);
    try {
      if (kind === "send") await api.post(`/invoices/${id}/send`);
      if (kind === "reminder") await api.post(`/invoices/${id}/reminder`);
      if (kind === "credit") await api.post(`/invoices/${id}/credit`);
      toast.success("Oppdatert");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };

  const registerPayment = async () => {
    setPaying(true);
    try {
      await api.post("/payments", { invoice_id: id, amount_nok: Number(amount), method: "bank" });
      toast.success("Innbetaling registrert");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setPaying(false); }
  };

  return (
    <div data-testid="faktura-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-400 hover:text-zinc-200 flex items-center gap-1 mb-3">
        <ArrowLeft className="h-3.5 w-3.5" /> Tilbake
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-heading text-2xl font-semibold tracking-tight">Faktura {inv.invoice_number}</h1>
            <StatusBadge value={inv.status} />
          </div>
          <div className="text-sm text-zinc-500">
            {inv.customer?.full_name} · KID {inv.kid} · utstedt {fmtDate(inv.issue_date)} · forfall {fmtDate(inv.due_date)}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={openPdf} className="h-9 px-3 rounded-md border border-zinc-900 hover:border-zinc-800 text-sm flex items-center gap-2" data-testid="btn-pdf">
            <FileText className="h-4 w-4" /> PDF
          </button>
          {inv.status === "utkast" && (
            <button onClick={() => action("send")} disabled={busy} className="h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm flex items-center gap-2" data-testid="btn-send">
              <Send className="h-4 w-4" /> Send faktura
            </button>
          )}
          {["sendt", "delvis_betalt"].includes(inv.status) && (
            <button onClick={() => action("reminder")} disabled={busy} className="h-9 px-3 rounded-md bg-orange-500/20 border border-orange-500/30 hover:bg-orange-500/30 text-orange-300 text-sm flex items-center gap-2" data-testid="btn-reminder">
              <AlertCircle className="h-4 w-4" /> Send purring
            </button>
          )}
          {inv.status !== "kreditert" && (
            <button onClick={() => action("credit")} disabled={busy} className="h-9 px-3 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm flex items-center gap-2" data-testid="btn-credit">
              <XCircle className="h-4 w-4" /> Krediter
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4"><div className="text-[11px] uppercase text-zinc-500 mb-1">Forbruk</div><div className="text-2xl font-heading tabular-nums">{fmtKwh(inv.consumption_kwh)}</div></Card>
        <Card className="p-4"><div className="text-[11px] uppercase text-zinc-500 mb-1">Snitt spotpris</div><div className="text-2xl font-heading tabular-nums">{fmtOre(inv.avg_spot_ore)}</div></Card>
        <Card className="p-4"><div className="text-[11px] uppercase text-zinc-500 mb-1">Total å betale</div><div className="text-2xl font-heading font-medium tabular-nums text-emerald-400">{fmtNOK(inv.total_nok)}</div></Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 overflow-hidden">
          <div className="p-4 border-b border-zinc-900 font-heading font-medium">Fakturalinjer</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-950/60">
                <th className="py-2 px-4 font-medium">Beskrivelse</th>
                <th className="py-2 px-4 font-medium text-right">Antall</th>
                <th className="py-2 px-4 font-medium">Enhet</th>
                <th className="py-2 px-4 font-medium text-right">Pris</th>
                <th className="py-2 px-4 font-medium text-right">Sum</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines?.map((l, i) => (
                <tr key={i} className="border-t border-zinc-900">
                  <td className="py-2 px-4 text-zinc-100">{l.description}</td>
                  <td className="py-2 px-4 tabular-nums text-right text-zinc-300">{l.quantity.toFixed(2)}</td>
                  <td className="py-2 px-4 text-zinc-400 text-xs">{l.unit}</td>
                  <td className="py-2 px-4 tabular-nums text-right text-zinc-300">{l.unit_price_nok.toFixed(4)}</td>
                  <td className="py-2 px-4 tabular-nums text-right text-zinc-100">{fmtNOK(l.amount_nok, { decimals: 2 })}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-zinc-800">
              <tr>
                <td colSpan="4" className="py-2 px-4 text-right text-zinc-400 text-xs">Sum eks. mva</td>
                <td className="py-2 px-4 tabular-nums text-right text-zinc-100">{fmtNOK(inv.subtotal_nok + inv.electricity_tax_nok, { decimals: 2 })}</td>
              </tr>
              <tr>
                <td colSpan="4" className="py-2 px-4 text-right text-zinc-400 text-xs">MVA (25 %)</td>
                <td className="py-2 px-4 tabular-nums text-right text-zinc-100">{fmtNOK(inv.vat_nok, { decimals: 2 })}</td>
              </tr>
              {inv.stromstotte_nok !== 0 && (
                <tr>
                  <td colSpan="4" className="py-2 px-4 text-right text-emerald-400 text-xs">Strømstøtte</td>
                  <td className="py-2 px-4 tabular-nums text-right text-emerald-400">{fmtNOK(inv.stromstotte_nok, { decimals: 2 })}</td>
                </tr>
              )}
              <tr className="bg-zinc-950/40">
                <td colSpan="4" className="py-3 px-4 text-right font-heading font-medium text-zinc-100">Totalt å betale</td>
                <td className="py-3 px-4 tabular-nums text-right font-heading font-medium text-emerald-400 text-lg">{fmtNOK(inv.total_nok, { decimals: 2 })}</td>
              </tr>
            </tfoot>
          </table>
        </Card>

        <Card className="p-4">
          <div className="font-heading font-medium mb-3">Registrer innbetaling</div>
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] uppercase text-zinc-500 mb-1">Beløp (NOK)</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm tabular-nums" data-testid="pay-amount" />
            </div>
            <button onClick={registerPayment} disabled={paying}
              className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2" data-testid="pay-submit">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Registrer betaling
            </button>
            <div className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Utestående: <span className="text-zinc-200 tabular-nums">{fmtNOK((inv.total_nok || 0) - (inv.paid_nok || 0))}</span>
              <br />Betalt: <span className="text-zinc-300 tabular-nums">{fmtNOK(inv.paid_nok || 0)}</span>
              <br />Purringer sendt: {inv.reminders_sent || 0}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
